// client/src/components/developers/DevelopersPage.jsx
//
// /:tenant/developers (Roadmap Phase 6), owners and admins:
//   - API keys: make one with chosen permissions (shown once), see when each
//     was last used, revoke
//   - Webhooks: send events to your own URL, signed; test, turn off, new
//     secret, delivery log with "send again"
//   - Links to the API docs (/developers) and openapi.json
//
// API: /api/developers/:tenant (server/routes/developerRoutes.js).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaKey, FaPlus, FaTrash, FaBook } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import {
  getDevelopersOverview, createApiKey, revokeApiKey, createWebhook, updateWebhook, deleteWebhook,
  rotateWebhookSecret, testWebhook, listDeliveries, redeliver, apiBaseUrl, errorText,
} from '../../services/developersApi';
import { RefreshButton } from '../common/IconButton';
import '../publishing/Publishing.css';
import '../donations/Donations.css';

function when(value) {
  if (!value) return '';
  const s = String(value);
  const d = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString();
}

/** A secret shown once, with a copy button. */
function ShowOnce({ label, value, note, onDone, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(value); setCopied(true); } catch { /* no clipboard */ } };
  return (
    <div className="note warn" role="status" data-testid="show-once">
      <strong>{label}</strong>
      <div className="dn-copy" style={{ marginTop: 6 }}>
        <input readOnly value={value} aria-label={label} onFocus={(e) => e.target.select()} />
        <button type="button" className="pb-btn" onClick={copy}>{copied ? t('developers.copied', 'Copied') : t('developers.copy', 'Copy')}</button>
      </div>
      <p className="pb-ai-meta" style={{ margin: '6px 0' }}>{note}</p>
      <button type="button" className="pb-btn" onClick={onDone}>{t('developers.saved', 'I’ve saved it')}</button>
    </div>
  );
}

export default function DevelopersPage({ tenantId }) {
  const params = useParams();
  const tenant = tenantId || params.tenant;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [keyForm, setKeyForm] = useState(null);
  const [hookForm, setHookForm] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const [deliveries, setDeliveries] = useState(null);

  const load = useCallback(() => getDevelopersOverview(tenant).then(setData).catch((err) => {
    if (err?.response?.status === 403) setDenied(true);
    else setError(errorText(err, t('developers.loadError', 'Developer settings could not be loaded.')));
  }), [tenant, t]);
  const loadDeliveries = useCallback(() => listDeliveries(tenant).then((r) => setDeliveries(r.deliveries)).catch(() => setDeliveries([])), [tenant]);
  useEffect(() => { load(); loadDeliveries(); }, [load, loadDeliveries]);

  const flash = (m) => { setNotice(m); setError(''); };
  const act = async (fn, okMsg, failMsg) => {
    try { const r = await fn(); if (okMsg) flash(okMsg); await load(); return r; } catch (err) { setError(errorText(err, failMsg)); return null; }
  };

  const makeKey = async (e) => {
    e.preventDefault();
    const r = await act(() => createApiKey(tenant, { name: keyForm.name, scopes: keyForm.scopes }), null, t('developers.keys.createError', 'The key could not be made.'));
    if (r) { setNewKey(r.key.key); setKeyForm(null); }
  };
  const makeHook = async (e) => {
    e.preventDefault();
    const r = await act(() => createWebhook(tenant, { url: hookForm.url, description: hookForm.description, events: hookForm.events }), null, t('developers.webhooks.createError', 'The webhook could not be added.'));
    if (r) { setNewSecret(r.webhook.secret); setHookForm(null); }
  };
  const runTest = async (w) => {
    try {
      const r = await testWebhook(tenant, w.id);
      if (r.result.ok) flash(t('developers.webhooks.testOk', 'Test sent: your URL answered {{status}}.', { status: r.result.httpStatus }));
      else setError(t('developers.webhooks.testFail', 'Test failed: {{error}}', { error: r.result.error }));
    } catch (err) { setError(errorText(err, t('developers.webhooks.testFail', 'Test failed: {{error}}', { error: '' }))); }
  };

  const buttons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaBook />, label: t('developers.docs', 'API docs'), onClick: () => window.open('/developers', '_blank', 'noopener') },
  ];
  const scopes = data ? Object.entries(data.scopes) : [];
  const events = data ? Object.entries(data.events) : [];
  const statusLabel = {
    pending: t('developers.deliveries.pending', 'Waiting'), sending: t('developers.deliveries.sending', 'Sending'), retry: t('developers.deliveries.retry', 'Will retry'),
    delivered: t('developers.deliveries.delivered', 'Delivered'), failed: t('developers.deliveries.failed', 'Failed'),
  };

  return (
    <div className="pb-page">
      <DesktopActionBar buttons={buttons} />
      <section className="surface">
        <h2>{t('developers.title', 'Developers')}</h2>
        <p className="surface-muted">
          {t('developers.intro', 'Connect On-Screen to your other tools. API keys let a program read and create your events, posts, tickets, members and donations; webhooks tell your systems the moment something happens. Zapier uses both.')}{' '}
          <a href="/developers" target="_blank" rel="noopener noreferrer">{t('developers.readDocs', 'Read the API docs')}</a>
        </p>
        <p className="pb-ai-meta" style={{ margin: 0 }}>{t('developers.baseUrl', 'API address:')} <code>{apiBaseUrl()}</code></p>
      </section>

      {!data && !error && !denied && <LoadingState />}
      {denied && <div className="note info" role="status">{t('developers.denied', 'Owners and admins manage API keys and webhooks.')}</div>}
      {error && <div className="note error" role="alert">{error}</div>}
      {notice && <div className="note ok" role="status">{notice}</div>}
      {data && !data.ready && <div className="note warn" role="status">{t('developers.migration', 'The API needs a database update before it can be used.')}</div>}
      {data && data.ready && !data.planAllows && <div className="note warn" role="status">{t('developers.plan', 'The API and webhooks aren’t part of your plan.')}</div>}

      {data && data.ready && (
        <>
          <section className="surface" data-testid="keys">
            <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}><FaKey aria-hidden="true" /> {t('developers.keys.title', 'API keys')}</h3>
              {!keyForm && <button type="button" className="pb-btn primary" onClick={() => setKeyForm({ name: '', scopes: [] })}><FaPlus aria-hidden="true" /> {t('developers.keys.new', 'New API key')}</button>}
            </div>
            <p className="pb-ai-meta">{t('developers.keys.help', 'Each key can only do what you tick, and is limited to {{n}} requests a minute. Treat a key like a password; revoke it if it might have leaked.', { n: data.limits.requestsPerMinute })}</p>
            {newKey && <ShowOnce t={t} label={t('developers.keys.yourKey', 'Your new API key')} value={newKey} note={t('developers.keys.once', 'Copy it now: it won’t be shown again.')} onDone={() => setNewKey(null)} />}
            {keyForm && (
              <form className="pb-form" onSubmit={makeKey} data-testid="key-form">
                <label className="pb-field" htmlFor="dk-name">
                  {t('developers.keys.name', 'Name (what uses it)')}
                  <input id="dk-name" value={keyForm.name} onChange={(e) => setKeyForm((f) => ({ ...f, name: e.target.value }))} placeholder="Zapier" required maxLength={100} />
                </label>
                <fieldset className="pb-checks pb-wide">
                  <legend>{t('developers.keys.scopes', 'Permissions')}</legend>
                  {scopes.map(([s, label]) => (
                    <label key={s}>
                      <input type="checkbox" checked={keyForm.scopes.includes(s)}
                        onChange={(e) => setKeyForm((f) => ({ ...f, scopes: e.target.checked ? [...f.scopes, s] : f.scopes.filter((x) => x !== s) }))} />
                      {' '}{label} <code>{s}</code>
                    </label>
                  ))}
                </fieldset>
                <div className="pb-actions pb-wide">
                  <button type="submit" className="pb-btn primary" disabled={!keyForm.name || !keyForm.scopes.length}>{t('developers.keys.create', 'Make the key')}</button>
                  <button type="button" className="pb-btn" onClick={() => setKeyForm(null)}>{t('common.cancel', 'Cancel')}</button>
                </div>
              </form>
            )}
            {data.keys.length === 0 && !keyForm && <p className="pb-ai-meta">{t('developers.keys.empty', 'No API keys yet.')}</p>}
            {data.keys.length > 0 && (
              <div className="pb-table-wrap">
                <table className="pb-table">
                  <thead><tr>
                    <th>{t('developers.keys.nameCol', 'Name')}</th><th>{t('developers.keys.key', 'Key')}</th><th>{t('developers.keys.scopes', 'Permissions')}</th>
                    <th>{t('developers.keys.lastUsed', 'Last used')}</th><th>{t('developers.actions', 'Actions')}</th>
                  </tr></thead>
                  <tbody>
                    {data.keys.map((k) => (
                      <tr key={k.id} data-testid="key-row">
                        <td>{k.name}</td><td><code>{k.hint}</code></td><td>{k.scopes.join(', ')}</td>
                        <td>{k.revokedOn ? t('developers.keys.revoked', 'Revoked') : (k.lastUsedOn ? when(k.lastUsedOn) : t('developers.keys.never', 'Never'))}</td>
                        <td>{!k.revokedOn && (
                          <button type="button" className="pb-btn danger" onClick={() => window.confirm(t('developers.keys.confirmRevoke', 'Revoke “{{name}}”? Anything using it stops working, including webhooks it subscribed.', { name: k.name })) && act(() => revokeApiKey(tenant, k.id), t('developers.keys.revokedMsg', 'Key revoked.'), t('developers.keys.revokeError', 'The key could not be revoked.'))}>
                            {t('developers.keys.revoke', 'Revoke')}
                          </button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="surface" data-testid="webhooks">
            <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{t('developers.webhooks.title', 'Webhooks')}</h3>
              {!hookForm && <button type="button" className="pb-btn primary" onClick={() => setHookForm({ url: '', description: '', events: [] })}><FaPlus aria-hidden="true" /> {t('developers.webhooks.new', 'Add a webhook')}</button>}
            </div>
            <p className="pb-ai-meta">{t('developers.webhooks.help', 'We POST a signed JSON message to your https URL when something happens, and retry for up to a day if it fails. Check the OnScreen-Signature header with the webhook’s secret (see the API docs).')}</p>
            {newSecret && <ShowOnce t={t} label={t('developers.webhooks.yourSecret', 'Signing secret')} value={newSecret} note={t('developers.webhooks.once', 'Copy it now: use it to check that messages really come from On-Screen. It won’t be shown again.')} onDone={() => setNewSecret(null)} />}
            {hookForm && (
              <form className="pb-form" onSubmit={makeHook} data-testid="webhook-form">
                <label className="pb-field pb-wide" htmlFor="dw-url">
                  {t('developers.webhooks.url', 'URL (https)')}
                  <input id="dw-url" type="url" value={hookForm.url} onChange={(e) => setHookForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://" required />
                </label>
                <label className="pb-field pb-wide" htmlFor="dw-desc">
                  {t('developers.webhooks.description', 'Label')}
                  <input id="dw-desc" value={hookForm.description} onChange={(e) => setHookForm((f) => ({ ...f, description: e.target.value }))} maxLength={200} />
                </label>
                <fieldset className="pb-checks pb-wide">
                  <legend>{t('developers.webhooks.events', 'Send these events')}</legend>
                  {events.map(([ev, label]) => (
                    <label key={ev}>
                      <input type="checkbox" checked={hookForm.events.includes(ev)}
                        onChange={(e) => setHookForm((f) => ({ ...f, events: e.target.checked ? [...f.events, ev] : f.events.filter((x) => x !== ev) }))} />
                      {' '}{label} <code>{ev}</code>
                    </label>
                  ))}
                </fieldset>
                <div className="pb-actions pb-wide">
                  <button type="submit" className="pb-btn primary" disabled={!hookForm.url || !hookForm.events.length}>{t('developers.webhooks.add', 'Add webhook')}</button>
                  <button type="button" className="pb-btn" onClick={() => setHookForm(null)}>{t('common.cancel', 'Cancel')}</button>
                </div>
              </form>
            )}
            {data.webhooks.length === 0 && !hookForm && <p className="pb-ai-meta">{t('developers.webhooks.empty', 'No webhooks yet.')}</p>}
            {data.webhooks.map((w) => (
              <article key={w.id} className="pb-feed" data-testid="webhook">
                <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
                  <p className="pb-feed-title" style={{ margin: 0, wordBreak: 'break-all' }}>{w.url}</p>
                  <span className={`pb-badge ${w.active ? (w.failingSince ? 'warn' : 'ok') : 'muted'}`}>
                    {!w.active ? t('developers.webhooks.off', 'Off') : w.failingSince ? t('developers.webhooks.failing', 'Failing') : t('developers.webhooks.on', 'On')}
                  </span>
                </div>
                <p className="pb-feed-meta" style={{ margin: '4px 0' }}>
                  {w.events.join(', ')}{w.description && ` · ${w.description}`}{w.source === 'api' && ` · ${t('developers.webhooks.viaApi', 'added through the API')}`}
                </p>
                {w.disabledReason && <p className="pb-ai-meta" style={{ margin: '0 0 4px' }}>{w.disabledReason}</p>}
                <div className="pb-actions">
                  <button type="button" className="pb-btn" onClick={() => runTest(w)}>{t('developers.webhooks.test', 'Send a test')}</button>
                  <button type="button" className="pb-btn" onClick={() => act(() => updateWebhook(tenant, w.id, { active: !w.active }), w.active ? t('developers.webhooks.turnedOff', 'Webhook turned off.') : t('developers.webhooks.turnedOn', 'Webhook turned on.'), t('developers.webhooks.saveError', 'The webhook could not be changed.'))}>
                    {w.active ? t('developers.webhooks.turnOff', 'Turn off') : t('developers.webhooks.turnOn', 'Turn on')}
                  </button>
                  <button type="button" className="pb-btn" onClick={async () => { const r = await act(() => rotateWebhookSecret(tenant, w.id), null, t('developers.webhooks.saveError', 'The webhook could not be changed.')); if (r) setNewSecret(r.secret); }}>
                    {t('developers.webhooks.rotate', 'New secret')}
                  </button>
                  <button type="button" className="pb-btn danger" onClick={() => window.confirm(t('developers.webhooks.confirmDelete', 'Remove this webhook?')) && act(() => deleteWebhook(tenant, w.id), t('developers.webhooks.deleted', 'Webhook removed.'), t('developers.webhooks.saveError', 'The webhook could not be changed.'))}>
                    <FaTrash aria-hidden="true" /> {t('developers.webhooks.delete', 'Remove')}
                  </button>
                </div>
              </article>
            ))}
          </section>

          <section className="surface" data-testid="deliveries">
            <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{t('developers.deliveries.title', 'Recent deliveries')}</h3>
              <RefreshButton onClick={loadDeliveries} label={t('developers.deliveries.refresh', 'Refresh')} />
            </div>
            {!deliveries && <LoadingState />}
            {deliveries && deliveries.length === 0 && <p className="pb-ai-meta">{t('developers.deliveries.empty', 'Nothing sent yet.')}</p>}
            {deliveries && deliveries.length > 0 && (
              <div className="pb-table-wrap">
                <table className="pb-table">
                  <thead><tr>
                    <th>{t('developers.deliveries.when', 'When')}</th><th>{t('developers.deliveries.event', 'Event')}</th><th>{t('developers.deliveries.status', 'Status')}</th>
                    <th>{t('developers.deliveries.response', 'Response')}</th><th>{t('developers.actions', 'Actions')}</th>
                  </tr></thead>
                  <tbody>
                    {deliveries.map((d) => (
                      <tr key={d.id}>
                        <td>{when(d.createdOn)}</td><td><code>{d.type}</code></td>
                        <td>{statusLabel[d.status] || d.status}{d.attempts > 1 && ` (${t('developers.deliveries.attempts', '{{n}} tries', { n: d.attempts })})`}</td>
                        <td>{d.lastStatus ? `HTTP ${d.lastStatus}` : (d.lastError || '—')}</td>
                        <td>{['failed', 'retry'].includes(d.status) && (
                          <button type="button" className="pb-btn" onClick={async () => { try { await redeliver(tenant, d.id); flash(t('developers.deliveries.resent', 'Sending again.')); loadDeliveries(); } catch (err) { setError(errorText(err, t('developers.deliveries.resendError', 'Could not send again.'))); } }}>
                            {t('developers.deliveries.resend', 'Send again')}
                          </button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
      <AppFooter buttons={buttons} />
    </div>
  );
}
